import React, { useState } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  SelectChangeEvent,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  Visibility,
  TouchApp,
  People,
  MonetizationOn,
  Campaign,
  Share,
  ArrowUpward,
  ArrowDownward,
  Download,
  DateRange,
} from '@mui/icons-material';
import { ResponsiveContainer, LineChart as ReLineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import { brandColors } from '../../theme';

interface MetricCardProps {
  title: string;
  value: string;
  change: string;
  trend: 'up' | 'down';
  icon: React.ReactNode;
  color: string;
}

interface CampaignPerformance {
  id: number;
  name: string;
  impressions: number;
  clicks: number;
  conversions: number;
  spend: number;
  ctr: number;
  cpa: number;
  roas: number;
  status: string;
}

const MetricCard: React.FC<MetricCardProps> = ({ title, value, change, trend, icon }) => (
  <Card sx={{ height: '100%', border: '1px solid var(--line)', backgroundColor: 'var(--card)', borderRadius: 'var(--radius)' }}>
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, color: 'var(--muted)' }}>
          <Box sx={{ width: 44, height: 44, borderRadius: 2, bgcolor: 'var(--slate-100)', color: 'var(--ink)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {icon}
          </Box>
          <Typography variant="body2" sx={{ fontWeight: 600, color: 'var(--muted)' }}>{title}</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {trend === 'up' ? (
            <ArrowUpward sx={{ color: 'var(--success)', fontSize: 18 }} />
          ) : (
            <ArrowDownward sx={{ color: 'var(--danger)', fontSize: 18 }} />
          )}
          <Typography variant="caption" sx={{ fontWeight: 700, color: trend === 'up' ? 'var(--success)' : 'var(--danger)' }}>
            {change}
          </Typography>
        </Box>
      </Box>
      <Typography className="tabular-nums" sx={{ fontWeight: 700, color: 'var(--ink)', fontSize: '1.5rem' }}>
        {value}
      </Typography>
    </CardContent>
  </Card>
);

type MetricOption = 'impressions' | 'clicks' | 'conversions' | 'spend';

const Analytics: React.FC = () => {
  const [timeRange, setTimeRange] = useState('30');
  const [campaignFilter, setCampaignFilter] = useState('all');
  const [selectedMetrics, setSelectedMetrics] = useState<MetricOption[]>(['impressions']);

  const campaignPerformance: CampaignPerformance[] = [
    {
      id: 1,
      name: 'Sommer Handwerker Kampagne',
      impressions: 1234567,
      clicks: 45234,
      conversions: 2876,
      spend: 18500,
      ctr: 3.67,
      cpa: 6.43,
      roas: 4.2,
      status: 'active'
    },
    {
      id: 2,
      name: 'Herbst Promotion 2025',
      impressions: 987654,
      clicks: 32156,
      conversions: 1845,
      spend: 12300,
      ctr: 3.26,
      cpa: 6.67,
      roas: 3.8,
      status: 'scheduled'
    },
    {
      id: 3,
      name: 'Weihnachts Special',
      impressions: 2345678,
      clicks: 89123,
      conversions: 5234,
      spend: 28900,
      ctr: 3.80,
      cpa: 5.52,
      roas: 5.1,
      status: 'active'
    },
  ];

  const channelPerformance = [
    { channel: 'Meta (Facebook/Instagram)', impressions: 1500000, clicks: 67500, conversions: 4200, spend: 22000, color: brandColors.electricTeal },
    { channel: 'Google Ads', impressions: 1200000, clicks: 48000, conversions: 3100, spend: 18500, color: brandColors.neonLime },
    { channel: 'Print Media', impressions: 500000, clicks: 15000, conversions: 1200, spend: 8500, color: brandColors.midnightSteel },
    { channel: 'Radio', impressions: 800000, clicks: 12000, conversions: 950, spend: 6700, color: '#ff9800' },
  ];

  const handleTimeRangeChange = (event: SelectChangeEvent) => {
    setTimeRange(event.target.value);
  };

  const handleCampaignFilterChange = (event: SelectChangeEvent) => {
    setCampaignFilter(event.target.value);
  };

  const getDaysFromRange = (range: string) => {
    switch (range) {
      case '7':
        return 7;
      case '30':
        return 30;
      case '90':
        return 90;
      case '365':
        return 365;
      default:
        return 30;
    }
  };

  const getMetricColor = (metric: MetricOption) => {
    switch (metric) {
      case 'impressions':
        return brandColors.electricTeal;
      case 'clicks':
        return brandColors.neonLime;
      case 'conversions':
        return brandColors.midnightSteel;
      case 'spend':
        return brandColors.electricTeal;
      default:
        return brandColors.electricTeal;
    }
  };
  
  type ChartPoint = { label: string } & Record<MetricOption, number>;

  const generateSeries = (days: number): ChartPoint[] => {
    const points: ChartPoint[] = [];
    const today = new Date();

    const baseMap: Record<MetricOption, number> = {
      impressions: 95000,
      clicks: 4200,
      conversions: 260,
      spend: 1900,
    };
    const amplitudeMap: Record<MetricOption, number> = {
      impressions: 25000,
      clicks: 1200,
      conversions: 90,
      spend: 500,
    };
    const trendMap: Record<MetricOption, number> = {
      impressions: 18 / days,
      clicks: 12 / days,
      conversions: 8 / days,
      spend: -3 / days,
    };

    for (let i = days - 1; i >= 0; i -= 1) {
      const d = new Date(today);
      d.setDate(today.getDate() - i);
      const label = d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
      const point: ChartPoint = { label, impressions: 0, clicks: 0, conversions: 0, spend: 0 };

      (['impressions', 'clicks', 'conversions', 'spend'] as MetricOption[]).forEach((metric) => {
        const idx = days - 1 - i;
        const wave = Math.sin((idx / Math.max(6, days / 8)) * Math.PI * 2) * 0.6 + Math.cos((idx / Math.max(10, days / 12)) * Math.PI * 2) * 0.4;
        const noise = Math.sin(idx * 1.7) * 0.15;
        const trendFactor = 1 + (idx * trendMap[metric]) / 100;
        point[metric] = Math.max(0, Math.round((baseMap[metric] + amplitudeMap[metric] * (wave + noise)) * trendFactor));
      });

      points.push(point);
    }

    return points;
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  const getPerformanceColor = (value: number, type: 'ctr' | 'roas') => {
    if (type === 'ctr') {
      return value >= 3.5 ? '#4caf50' : value >= 2.5 ? '#ff9800' : '#f44336';
    }
    return value >= 4 ? '#4caf50' : value >= 3 ? '#ff9800' : '#f44336';
  };

  return (
    <Box>
      {/* Header Section (neutral) */}
      <Box sx={{ mb: 4, p: 3, borderRadius: 'var(--radius)', border: '1px solid var(--line)', backgroundColor: 'var(--card)' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h2" sx={{ fontWeight: 600, mb: 1 }}>
              Analytics Dashboard
            </Typography>
            <Typography variant="body1" sx={{ color: 'var(--muted)' }}>
              Verfolgen Sie die Performance Ihrer Kampagnen in Echtzeit
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Zeitraum</InputLabel>
              <Select value={timeRange} onChange={handleTimeRangeChange} label="Zeitraum">
                <MenuItem value="7">7 Tage</MenuItem>
                <MenuItem value="30">30 Tage</MenuItem>
                <MenuItem value="90">90 Tage</MenuItem>
                <MenuItem value="365">1 Jahr</MenuItem>
              </Select>
            </FormControl>
            <Button variant="outlined" startIcon={<Download />}>Export</Button>
          </Box>
        </Box>
      </Box>
      {/* Key Metrics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 3
          }}>
          <MetricCard title="Gesamt Impressions" value="3.2M" change="+18%" trend="up" icon={<Visibility />} color={brandColors.electricTeal} />
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 3
          }}>
          <MetricCard title="Gesamt Clicks" value="142K" change="+12%" trend="up" icon={<TouchApp />} color={brandColors.neonLime} />
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 3
          }}>
          <MetricCard title="Conversions" value="8.1K" change="+25%" trend="up" icon={<People />} color={brandColors.midnightSteel} />
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 3
          }}>
          <MetricCard title="ROAS" value="4.3x" change="-3%" trend="down" icon={<MonetizationOn />} color={brandColors.electricTeal} />
        </Grid>
      </Grid>
      {/* Charts and Performance Overview */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid
          size={{
            xs: 12,
            md: 8
          }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: brandColors.midnightSteel }}>
                  Performance Verlauf
                </Typography>
                <FormControl size="small" sx={{ minWidth: 220 }}>
                  <InputLabel>Metriken</InputLabel>
                  <Select
                    multiple
                    value={selectedMetrics}
                    onChange={(event) => {
                      const raw = (event.target as { value: unknown }).value as unknown;
                      const value = (Array.isArray(raw) ? raw : [raw]) as MetricOption[];
                      setSelectedMetrics(value);
                    }}
                    label="Metriken"
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {(selected as string[]).map((value) => (
                          <Chip key={value} label={value} size="small" sx={{ bgcolor: `${getMetricColor(value as MetricOption)}20`, color: getMetricColor(value as MetricOption) }} />
                        ))}
                      </Box>
                    )}
                  >
                    <MenuItem value="impressions">Impressions</MenuItem>
                    <MenuItem value="clicks">Clicks</MenuItem>
                    <MenuItem value="conversions">Conversions</MenuItem>
                    <MenuItem value="spend">Ausgaben</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              {(() => {
                const days = getDaysFromRange(timeRange);
                const data = generateSeries(days);
                const yTickFormatter = (value: number) => formatNumber(value);
                const tooltipFormatter = (value: number, name: string) => {
                  if (name === 'spend') return [`€${formatNumber(value)}`, 'Ausgaben'];
                  const labelMap: Record<string, string> = { impressions: 'Impressions', clicks: 'Clicks', conversions: 'Conversions' };
                  return [formatNumber(value), labelMap[name] ?? name];
                };
                const tooltipLabelFormatter = (label: string) => `${label}`;
                return (
                  <Box sx={{ bgcolor: '#f9f9f9', borderRadius: 2, p: 1.5, mb: 2 }}>
                    <Box sx={{ height: 300 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <ReLineChart data={data} margin={{ left: 0, right: 8, top: 10, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--slate-200)" />
                          <XAxis dataKey="label" tickLine={false} axisLine={{ stroke: 'var(--line)' }} />
                          <YAxis tickFormatter={yTickFormatter} tickLine={false} axisLine={{ stroke: 'var(--line)' }} width={40} />
                          <Tooltip formatter={tooltipFormatter as any} labelFormatter={tooltipLabelFormatter} cursor={{ stroke: 'var(--line)' }} />
                          <Legend />
                          {selectedMetrics.map((metric) => (
                            <Line
                              key={metric}
                              type="monotone"
                              dataKey={metric}
                              name={metric === 'spend' ? 'Ausgaben' : metric.charAt(0).toUpperCase() + metric.slice(1)}
                              stroke={getMetricColor(metric)}
                              strokeWidth={2}
                              dot={false}
                              activeDot={{ r: 4 }}
                              isAnimationActive={true}
                            />
                          ))}
                        </ReLineChart>
                      </ResponsiveContainer>
                    </Box>
                  </Box>
                );
              })()}
              <Grid container spacing={2}>
                <Grid size={3}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h6" sx={{ color: brandColors.electricTeal, fontWeight: 700 }}>
                      3.8%
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Durchschn. CTR
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={3}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h6" sx={{ color: brandColors.neonLime, fontWeight: 700 }}>
                      €6.12
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Durchschn. CPA
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={3}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h6" sx={{ color: brandColors.midnightSteel, fontWeight: 700 }}>
                      5.7%
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Conversion Rate
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={3}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h6" sx={{ color: brandColors.electricTeal, fontWeight: 700 }}>
                      €59.7K
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Gesamt Ausgaben
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid
          size={{
            xs: 12,
            md: 4
          }}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: brandColors.midnightSteel }}>
                Top Performing Kanäle
              </Typography>
              {channelPerformance.map((channel, index) => (
                <Box key={index} sx={{ mb: 2, p: 2, bgcolor: '#f9f9f9', borderRadius: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {channel.channel}
                    </Typography>
                    <Typography variant="body2" sx={{ color: channel.color, fontWeight: 600 }}>
                      {formatNumber(channel.conversions)} Conv.
                    </Typography>
                  </Box>
                  <LinearProgress variant="determinate" value={(channel.conversions / 5234) * 100} sx={{ height: 6, borderRadius: 3, backgroundColor: 'var(--slate-100)', '& .MuiLinearProgress-bar': { backgroundColor: 'var(--brand)' } }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      {formatNumber(channel.impressions)} Imp.
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      €{formatNumber(channel.spend)} Ausgaben
                    </Typography>
                  </Box>
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      {/* Campaign Performance Table */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: brandColors.midnightSteel }}>
              Kampagnen Performance
            </Typography>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Kampagne Filter</InputLabel>
              <Select
                value={campaignFilter}
                onChange={handleCampaignFilterChange}
                label="Kampagne Filter"
              >
                <MenuItem value="all">Alle Kampagnen</MenuItem>
                <MenuItem value="active">Nur Aktive</MenuItem>
                <MenuItem value="scheduled">Nur Geplante</MenuItem>
              </Select>
            </FormControl>
          </Box>
          
          <TableContainer component={Paper} elevation={0}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><Typography variant="subtitle2" fontWeight={600}>Kampagne</Typography></TableCell>
                  <TableCell align="right"><Typography variant="subtitle2" fontWeight={600}>Impressions</Typography></TableCell>
                  <TableCell align="right"><Typography variant="subtitle2" fontWeight={600}>Clicks</Typography></TableCell>
                  <TableCell align="right"><Typography variant="subtitle2" fontWeight={600}>CTR</Typography></TableCell>
                  <TableCell align="right"><Typography variant="subtitle2" fontWeight={600}>Conversions</Typography></TableCell>
                  <TableCell align="right"><Typography variant="subtitle2" fontWeight={600}>CPA</Typography></TableCell>
                  <TableCell align="right"><Typography variant="subtitle2" fontWeight={600}>ROAS</Typography></TableCell>
                  <TableCell align="right"><Typography variant="subtitle2" fontWeight={600}>Ausgaben</Typography></TableCell>
                  <TableCell><Typography variant="subtitle2" fontWeight={600}>Status</Typography></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {campaignPerformance.map((campaign) => (
                  <TableRow key={campaign.id} hover>
                    <TableCell>
                      <Typography variant="subtitle1" fontWeight={600}>{campaign.name}</Typography>
                    </TableCell>
                    <TableCell align="right">{formatNumber(campaign.impressions)}</TableCell>
                    <TableCell align="right">{formatNumber(campaign.clicks)}</TableCell>
                    <TableCell align="right">
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: getPerformanceColor(campaign.ctr, 'ctr'),
                          fontWeight: 600
                        }}
                      >
                        {campaign.ctr}%
                      </Typography>
                    </TableCell>
                    <TableCell align="right">{formatNumber(campaign.conversions)}</TableCell>
                    <TableCell align="right">€{campaign.cpa}</TableCell>
                    <TableCell align="right">
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: getPerformanceColor(campaign.roas, 'roas'),
                          fontWeight: 600
                        }}
                      >
                        {campaign.roas}x
                      </Typography>
                    </TableCell>
                    <TableCell align="right">€{formatNumber(campaign.spend)}</TableCell>
                    <TableCell>
                      <Chip 
                        label={campaign.status === 'active' ? 'Aktiv' : 'Geplant'} 
                        size="small"
                        sx={{ 
                          bgcolor: campaign.status === 'active' ? `${brandColors.electricTeal}20` : `${brandColors.neonLime}20`,
                          color: campaign.status === 'active' ? brandColors.electricTeal : brandColors.neonLime,
                          fontWeight: 600
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Analytics;
